/**
 * Boot timing marks — three numbers, always logged.
 *
 * Two cards in a row were answered by arithmetic on request counts instead of a
 * measurement: one night went into the discovery sweep and four commits into the
 * transport ladder before the renderer's own timeline was read, and what it said
 * was *"the HTML arrived in 1 s and the module graph took 11.9 s more"*
 * (t_59326081, t_3b15c05b). The three numbers that answer "why did this take ten
 * seconds" are:
 *
 *   responseEnd        the document arrived — the browser's own navigation mark
 *   domContentLoaded   the module graph finished (in dev: vite's unbundled
 *                      modules, queued six at a time — this is the ~13 s)
 *   firstHubResponse   the app got a reply from a hub
 *
 * They are logged as one line, at most twice: once when the DOM is ready (the hub
 * may not have answered yet) and once when the hub answers. A regression is then
 * a number in the log — `[Boot] responseEnd=993ms | domContentLoaded=12850ms |
 * firstHubResponse=13605ms (+755ms after DOMContentLoaded) /api/auth/session-token
 * → 200 via ipc` — and never again something to infer from a count.
 *
 * What counts as a hub answer: a response to a *hub-bound* request
 * (`otto-local://…`, or any path under `/api/`). The mark is placed where the
 * app's hub clients hand the response back — `services/apiRouter.ts`
 * (`electronSafeFetch` + the routed channel), `services/api.ts`
 * (`electronProxyFetch`) and `stores/auth.ts` (`runtimeFetch`, the boot-time
 * auth/setup path). A future client that bypasses all of them is not counted, so
 * when a boot number looks wrong, check that list before doubting the numbers.
 * Electron's main process already forwards renderer console lines
 * (`[Renderer:log] …`), which is where this ends up next to `[ElectronHTTP …]`.
 *
 * The clock is the page's own time origin (`performance.now()`), the same base
 * the navigation timings use, so the three numbers are directly comparable.
 *
 * Run: `npm test` (Node's own runner with tsx as loader; there is no test
 * framework in this repo). One file: `node --import tsx --test
 * src/utils/bootTiming.test.ts`.
 */

export type HubTransport = 'fetch' | 'ipc' | 'channel';

export interface HubAnswer {
  url: string;
  status: number;
  via: HubTransport;
  /** ms since the page's time origin. */
  atMs: number;
}

export interface BootMarks {
  responseEnd: number | null;
  domContentLoaded: number | null;
  firstHubResponse: HubAnswer | null;
}

export interface BootTimingEnv {
  /** ms since the page's time origin (`performance.now()`). */
  now: () => number;
  /** The navigation entry — the browser records these marks, we only read them. */
  navigation: () => { responseEnd?: number; domContentLoadedEventEnd?: number } | null;
  /** Calls back once, when the DOM has finished parsing (now, if it already has). */
  whenDomContentLoaded: (cb: () => void) => void;
  log: (line: string) => void;
  /** Optional: put the hub-answer moment on the DevTools timeline as well. */
  mark?: (name: string) => void;
}

export interface BootTiming {
  marks: () => BootMarks;
  /** Record a reply from a hub. The first one wins; later calls are ignored. */
  hubResponse: (url: string, status: number, via?: HubTransport) => void;
}

const PENDING = 'pending';
const MISSING = 'n/a';

const HUB_SCHEME = /^otto-local:\/\//i;
/**
 * The hub's API surface, on any origin: the LAN address, `localhost:16161`, the
 * dev server's proxy, or the cloud origin (where a hub path is carried over the
 * data channel — a boot in that mode counts the channel's answer).
 */
const HUB_API_PATH = /\/api(?:[/?#]|$)/;

export function isHubBoundUrl(url: string): boolean {
  return Boolean(url) && (HUB_SCHEME.test(url) || HUB_API_PATH.test(url));
}

/** The path without its query/hash — a log line reads better without `?t=…`. */
export function pathOf(url: string): string {
  return String(url).split('#')[0].split('?')[0];
}

function msText(value: number | null, fallback: string): string {
  return value === null ? fallback : `${Math.round(value)}ms`;
}

/** `+755ms after DOMContentLoaded`, or after `responseEnd` when the DOM is not ready yet. */
function deltaText(marks: BootMarks, atMs: number): string | null {
  const relative = (base: number, label: string): string => {
    const delta = Math.round(atMs - base);
    return delta < 0 ? `${-delta}ms before ${label}` : `+${delta}ms after ${label}`;
  };
  if (marks.domContentLoaded !== null) return relative(marks.domContentLoaded, 'DOMContentLoaded');
  if (marks.responseEnd !== null) return relative(marks.responseEnd, 'responseEnd');
  return null;
}

/** One line, always the same three fields — that is what makes a regression greppable. */
export function formatBootLine(marks: BootMarks): string {
  const parts = [
    `responseEnd=${msText(marks.responseEnd, MISSING)}`,
    `domContentLoaded=${msText(marks.domContentLoaded, PENDING)}`,
  ];
  const hub = marks.firstHubResponse;
  if (!hub) {
    parts.push(`firstHubResponse=${PENDING}`);
  } else {
    const delta = deltaText(marks, hub.atMs);
    parts.push(
      `firstHubResponse=${Math.round(hub.atMs)}ms${delta ? ` (${delta})` : ''}` +
        ` ${pathOf(hub.url)} → ${hub.status} via ${hub.via}`,
    );
  }
  return `[Boot] ${parts.join(' | ')}`;
}

export function createBootTiming(env: BootTimingEnv): BootTiming {
  const marks: BootMarks = { responseEnd: null, domContentLoaded: null, firstHubResponse: null };
  let emitted = '';

  const emit = () => {
    const line = formatBootLine(marks);
    if (line === emitted) return;
    emitted = line;
    env.log(line);
  };

  const readNavigation = () => {
    const nav = env.navigation();
    if (!nav) return;
    if (typeof nav.responseEnd === 'number' && nav.responseEnd > 0) {
      marks.responseEnd = nav.responseEnd;
    }
    // 0 means "DOMContentLoaded has not fired yet" — that is what the callback below waits for.
    if (typeof nav.domContentLoadedEventEnd === 'number' && nav.domContentLoadedEventEnd > 0) {
      marks.domContentLoaded = nav.domContentLoadedEventEnd;
    }
  };

  readNavigation();
  env.whenDomContentLoaded(() => {
    readNavigation();
    emit();
  });

  return {
    marks: () => ({
      ...marks,
      firstHubResponse: marks.firstHubResponse ? { ...marks.firstHubResponse } : null,
    }),
    hubResponse(url, status, via = 'fetch') {
      if (marks.firstHubResponse) return;
      marks.firstHubResponse = { url, status, via, atMs: env.now() };
      // A request fired before the DOM is ready answers before it too.
      readNavigation();
      try {
        env.mark?.('otto:boot:first-hub-response');
      } catch {
        /* User Timing is a convenience, never a requirement */
      }
      emit();
    },
  };
}

function browserEnv(): BootTimingEnv {
  return {
    now: () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
    navigation: () => {
      if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') return null;
      const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      return entry
        ? { responseEnd: entry.responseEnd, domContentLoadedEventEnd: entry.domContentLoadedEventEnd }
        : null;
    },
    whenDomContentLoaded: (cb) => {
      if (typeof document === 'undefined') return;
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cb, { once: true });
      else cb();
    },
    log: (line) => console.log(line),
    mark: (name) => {
      try {
        performance.mark(name);
      } catch {
        /* not available in this engine */
      }
    },
  };
}

let timing: BootTiming | null = null;

/** Install once, as early in the app entry as possible (`src/main.ts`). */
export function installBootTiming(overrides: Partial<BootTimingEnv> = {}): BootTiming {
  if (!timing) {
    timing = createBootTiming({ ...browserEnv(), ...overrides });
    if (typeof window !== 'undefined') {
      // Read by harnesses (scripts/measure-boot.mjs) — the log line is for humans.
      (window as unknown as Record<string, unknown>).__ottoBootMarks = bootMarks;
    }
  }
  return timing;
}

export function bootMarks(): BootMarks {
  return timing?.marks() ?? { responseEnd: null, domContentLoaded: null, firstHubResponse: null };
}

/**
 * Record the reply of a hub-bound request. The first reply wins; a non-hub URL
 * (a CDN fetch, the dev server's own modules) or a failed transport (status 0)
 * is not an answer and is ignored.
 */
export function markHubResponse(url: string, status: number, via: HubTransport = 'fetch'): void {
  if (!timing || status <= 0 || !isHubBoundUrl(url)) return;
  timing.hubResponse(url, status, via);
}
